import { useEffect, useMemo, useRef, useState } from 'react';
import { ReactSortable } from 'react-sortablejs';
import { ReactComponent as ReorderIcon } from '../assets/images/icons/ic-reorder.svg';
import { ReactComponent as PinIcon } from '../assets/images/icons/ic-pin.svg';
import { Each } from '../common/Each';
import typo from '../typography.module.css';
import { formatDateV2, formatTimeV2 } from '../utils';
import Skeleton from './Skeleton';
import styles from './Table.module.css';

const formatColumnLabel = (label) => {
    if (typeof label !== 'string') {
        return label;
    }

    const normalized = label.toLocaleLowerCase('it-IT');
    return normalized.replace(/(^|\s)([a-z\u00C0-\u017F])/g, (_, prefix, letter) => `${prefix}${letter.toUpperCase()}`);
};

const Table = ({
    loading,
    loadingN,
    columns = [],
    elements = [],
    defaultSort = { key: 'id', direction: 'desc' },
    onRowClick,
    style = {},
    appearance = 'default',
    integrated = false,
    onSort,
    onContextMenu = () => { },
    selectedElementID,
    ordering = false,
    orderingHandles = false,
    orderingDisabled = false,
    onOrdering,
    expandedRowIds = [],
    renderExpandedRow = null,
}) => {
    const [currentSort, setCurrentSort] = useState(defaultSort);
    const [sortedElements, setSortedElements] = useState(elements);
    const [_columns, setColumns] = useState(columns);
    const containerRef = useRef(null);

    useEffect(() => {
        if (loading || !ordering || orderingHandles) return;

        const container = containerRef.current;
        if (!container) return;

        const anchors = container.querySelectorAll('a');
        function preventAnchor(e) {
            e.preventDefault();
            e.stopPropagation();
        }

        anchors.forEach((a) => {
            a.removeAttribute('href');
            a.setAttribute('tabindex', '-1');
            a.setAttribute('aria-disabled', 'true');
            a.addEventListener('click', preventAnchor, true);
        });

        return () => {
            anchors.forEach((a) => {
                a.removeAttribute('tabindex');
                a.removeAttribute('aria-disabled');
                a.removeEventListener('click', preventAnchor, true);
            });
        };
    }, [ordering, orderingHandles, loading]);

    useEffect(() => {
        setColumns(columns);
    }, [columns]);

    const sortedColumns = useMemo(() => {
        if (!_columns.find((c) => c.pinned)) {
            return [..._columns];
        }

        return [..._columns].sort((a, b) => {
            if (a.pinned && !b.pinned) return -1;
            if (!a.pinned && b.pinned) return 1;
            return 0;
        });
    }, [_columns]);

    const getSortedElements = (items, key, type, direction) => {
        return [...items].sort((a, b) => {
            if (direction === 'asc' && type === 'number') {
                return a[key] > b[key] ? 1 : -1;
            }
            if (direction === 'desc' && type === 'number') {
                return a[key] < b[key] ? 1 : -1;
            }
            if (direction === 'asc' && type === 'string') {
                return (a[key] || '').localeCompare(b[key] || '');
            }
            if (direction === 'desc' && type === 'string') {
                return (b[key] || '').localeCompare(a[key] || '');
            }
            if (direction === 'desc' && (type === 'date' || type === 'datetime')) {
                return new Date(b[key]) - new Date(a[key]);
            }
            if (direction === 'asc' && (type === 'date' || type === 'datetime')) {
                return new Date(a[key]) - new Date(b[key]);
            }
            if (direction === 'desc' && type === 'custom') {
                if (a[key]?.type === 'string' && b[key]?.type === 'string') {
                    return (b[key].value || '').localeCompare(a[key].value || '');
                }
                if (a[key]?.type === 'number' && b[key]?.type === 'number') {
                    return b[key].value - a[key].value;
                }
                return 0;
            }
            if (direction === 'asc' && type === 'custom') {
                if (a[key]?.type === 'string' && b[key]?.type === 'string') {
                    return (a[key].value || '').localeCompare(b[key].value || '');
                }
                if (a[key]?.type === 'number' && b[key]?.type === 'number') {
                    return a[key].value - b[key].value;
                }
                return 0;
            }
            return 0;
        });
    };

    const sortElements = (key, type) => {
        if (ordering && orderingHandles) return;
        const direction = currentSort.direction === 'asc' ? 'desc' : 'asc';

        if (onSort && typeof onSort === 'function') {
            onSort(key, direction);
            return;
        }

        setCurrentSort((prev) => ({ ...prev, key, direction }));
        setSortedElements(getSortedElements(elements, key, type, direction));
    };

    useEffect(() => {
        const currentColumn = sortedColumns.find((column) => column.key === currentSort.key);
        if (!currentColumn) {
            setSortedElements(elements);
            return;
        }

        const sorted = getSortedElements(elements, currentSort.key, currentColumn.type, currentSort.direction);
        setSortedElements(sorted);
    }, [elements, currentSort, sortedColumns]);

    const renderCell = (row, column, isLoading = false) => {
        let cellStyle = '';
        switch (column.type) {
            case 'string':
                cellStyle = styles.string;
                break;
            case 'number':
                cellStyle = styles.number;
                break;
            case 'date':
                cellStyle = styles.date;
                break;
            case 'custom':
                cellStyle = styles.custom;
                break;
            default:
                cellStyle = '';
        }

        if (isLoading) {
            return (
                <td key={column.key} className={column.pinned ? styles.fixed : ''}>
                    <a href={undefined} rel='noreferrer'>
                        <div className={cellStyle}>
                            <Skeleton height={20} width={'100%'} borderRadius={6} />
                        </div>
                    </a>
                </td>
            );
        }

        if (column.type === 'date' && row?.[column.key]) {
            return (
                <td key={column.key} className={column.pinned ? styles.fixed : ''}>
                    <a href={ordering && orderingHandles ? undefined : row?.href || undefined} rel='noreferrer'>
                        <div className={cellStyle} style={{ ...column.style, whiteSpace: column.wrap === true ? 'normal' : 'nowrap' }}>
                            {formatDateV2(row[column.key], column.options ?? { day: '2-digit', month: '2-digit', year: 'numeric' }, 'it', true, column.forceNumeric ?? true)}
                        </div>
                    </a>
                </td>
            );
        }

        if (column.type === 'datetime' && row?.[column.key]) {
            return (
                <td key={column.key} className={column.pinned ? styles.fixed : ''}>
                    <a href={ordering && orderingHandles ? undefined : row?.href || undefined} rel='noreferrer'>
                        <div className={cellStyle} style={{ ...column.style, whiteSpace: column.wrap === true ? 'normal' : 'nowrap' }}>
                            {formatDateV2(row[column.key], column.options ?? { day: '2-digit', month: '2-digit', year: 'numeric' }, 'it', true, column.forceNumeric ?? true)} - {formatTimeV2(row[column.key])}
                        </div>
                    </a>
                </td>
            );
        }

        if (column.type === 'custom') {
            return (
                <td key={column.key} className={column.pinned ? styles.fixed : ''}>
                    <div
                        className={cellStyle}
                        style={{ ...column.style, whiteSpace: column.wrap === true ? 'normal' : 'nowrap' }}
                        onClick={(ev) => {
                            ev.preventDefault();
                            if (column.handleClick) {
                                ev.stopPropagation();
                            }
                        }}
                    >
                        {row?.[column.key]?.component ?? row?.[column.key] ?? 'N/A'}
                    </div>
                </td>
            );
        }

        return (
            <td key={column.key} className={column.pinned ? styles.fixed : ''}>
                <a href={ordering && orderingHandles ? undefined : row?.href || undefined} rel='noreferrer'>
                    <div className={cellStyle} style={{ ...column.style, whiteSpace: column.wrap === true ? 'normal' : 'nowrap' }}>
                        {row?.[column.key] ?? 'N/A'}
                    </div>
                </a>
            </td>
        );
    };

    const containerClassName = [
        styles.container,
        appearance === 'minimal' ? styles.minimal : '',
        integrated ? styles.integrated : '',
        ordering && orderingHandles ? styles.ordering : '',
    ].filter(Boolean).join(' ');

    return (
        <div className={containerClassName} style={style} ref={containerRef}>
            <table>
                <thead style={{ position: 'sticky', top: 0, backgroundColor: 'var(--background-secondary-color)' }}>
                    <tr>
                        {ordering && orderingHandles && <th className={styles.handleCell} aria-label="Riordina righe" />}
                        <Each of={sortedColumns} render={(column) => (
                            <th key={column.key} className={column.pinned ? styles.fixed : ''}>
                                <div
                                    className={typo.caption}
                                    onClick={() => {
                                        sortElements(column.key, column.type);
                                    }}
                                >
                                    {formatColumnLabel(column.label)}
                                    {!(ordering && orderingHandles) && currentSort.key === column.key && <>{currentSort.direction === 'asc' ? '↑' : '↓'}</>}
                                </div>
                                {!(ordering && orderingHandles) && <PinIcon
                                    className={styles.pinIcon}
                                    onClick={() => setColumns(_columns.map((c) => (c.key === column.key ? { ...c, pinned: !c.pinned } : { ...c, pinned: false })))}
                                />}
                            </th>
                        )} />
                    </tr>
                </thead>

                {ordering ? (
                    <ReactSortable
                        tag='tbody'
                        list={orderingHandles ? elements : sortedElements}
                        setList={(elms) => {
                            if (orderingHandles) {
                                if (!orderingDisabled && elms.some((row, index) => row.id !== elements[index]?.id)) onOrdering?.(elms);
                                return;
                            }
                            setSortedElements(elms);
                            if (onOrdering && typeof onOrdering === 'function') {
                                onOrdering(elms);
                            }
                        }}
                        handle={orderingHandles ? `.${styles.dragHandle}` : 'tr'}
                        disabled={orderingDisabled}
                        scroll
                        bubbleScroll={false}
                        scrollSensitivity={60}
                        scrollSpeed={12}
                        ghostClass={styles.dragGhost}
                        animation={200}
                    >
                        {(orderingHandles ? elements : sortedElements).map((row, rowIndex) => (
                            <tr
                                key={row?.id ?? `row-${rowIndex}`}
                                className={[
                                    selectedElementID && selectedElementID === row?.id ? styles.selected : '',
                                    (typeof onRowClick === 'function' || Boolean(row?.href)) ? styles.clickable : '',
                                ].filter(Boolean).join(' ')}
                                onClick={() => {
                                    if (!(ordering && orderingHandles) && typeof onRowClick === 'function') {
                                        onRowClick(row);
                                    }
                                }}
                                onContextMenu={(event) => {
                                    onContextMenu(event, row);
                                }}
                                style={{ cursor: orderingHandles ? 'default' : 'grab' }}
                            >
                                {orderingHandles && <td className={styles.handleCell}>
                                    <button type="button" className={styles.dragHandle} disabled={orderingDisabled}
                                        aria-label={`Sposta riga ${rowIndex + 1}`}
                                        title="Trascina per riordinare o usa le frecce su e giù"
                                        onClick={event => event.stopPropagation()}
                                        onKeyDown={event => {
                                            if (!['ArrowUp', 'ArrowDown'].includes(event.key) || orderingDisabled) return;
                                            event.preventDefault();
                                            const target = rowIndex + (event.key === 'ArrowUp' ? -1 : 1);
                                            if (target < 0 || target >= elements.length) return;
                                            const next = [...elements];
                                            [next[rowIndex], next[target]] = [next[target], next[rowIndex]];
                                            onOrdering?.(next);
                                        }}>
                                        <ReorderIcon aria-hidden="true" />
                                    </button>
                                </td>}
                                {sortedColumns.map((column) => renderCell(row, column, false))}
                            </tr>
                        ))}
                    </ReactSortable>
                ) : (
                    <tbody>
                        <Each of={loading ? Array(loadingN ?? 25).fill(null) : sortedElements} render={(row, rowIndex) => (
                            <>
                                <tr
                                    key={row?.id ?? `row-${rowIndex}`}
                                    className={[
                                        selectedElementID && selectedElementID === row?.id ? styles.selected : '',
                                        ((typeof onRowClick === 'function' && !loading) || Boolean(row?.href)) ? styles.clickable : '',
                                    ].filter(Boolean).join(' ')}
                                    onClick={() => {
                                        if (!(ordering && orderingHandles) && typeof onRowClick === 'function') {
                                            onRowClick(row);
                                        }
                                    }}
                                    onContextMenu={(event) => {
                                        onContextMenu(event, row);
                                    }}
                                >
                                    {sortedColumns.map((column) => renderCell(row, column, loading))}
                                </tr>

                                {!loading && typeof renderExpandedRow === 'function' && expandedRowIds?.includes(row?.id) && (
                                    <tr className={styles.expandedRow}>
                                        <td colSpan={sortedColumns.length}>
                                            {renderExpandedRow(row)}
                                        </td>
                                    </tr>
                                )}
                            </>
                        )} />
                    </tbody>
                )}
            </table>
        </div>
    );
};

export default Table;
